import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Spin, Alert, Button, Breadcrumb } from 'antd';
import { HomeOutlined, LeftOutlined } from '@ant-design/icons';
import { getTopicById } from '@/constants/helpContent';
import { loadMarkdown, MarkdownLoadError } from '@/utils/loadMarkdown';
import { HelpContent } from '@/components/help/HelpContent';
import { useAuthStore } from '@/stores/authStore';

const { Content } = Layout;

/**
 * ヘルプ記事詳細ページ（/help/:articleId）
 *
 * 機能:
 * - 個別記事の表示
 * - Markdown読み込みとレンダリング
 * - パンくずリスト
 * - 認証チェック
 */
export function HelpArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();

  const [content, setContent] = useState('');
  const [error, setError] = useState<MarkdownLoadError | null>(null);
  const [loading, setLoading] = useState(true);

  const topic = articleId ? getTopicById(articleId) : undefined;

  // markdownPathからbaseImagePathを計算
  // 例: '/help/maintenance/calendar.md' -> '/help/maintenance'
  const baseImagePath = topic?.markdownPath
    ? topic.markdownPath.substring(0, topic.markdownPath.lastIndexOf('/'))
    : '/help';

  useEffect(() => {
    if (!articleId || !topic) {
      setError(new MarkdownLoadError('Topic not found', articleId || ''));
      setLoading(false);
      return;
    }

    // 認証チェック
    if (topic.requiresAuth && !isAuthenticated) {
      navigate('/login');
      return;
    }

    // ロールチェック
    if (topic.roles && !topic.roles.includes(user?.role || '')) {
      setError(new MarkdownLoadError('Access denied', articleId, 403));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    loadMarkdown(topic.markdownPath)
      .then(setContent)
      .catch((err) => {
        if (err instanceof MarkdownLoadError) {
          setError(err);
        } else {
          setError(new MarkdownLoadError('Unknown error', topic.markdownPath));
        }
      })
      .finally(() => setLoading(false));
  }, [articleId, topic, isAuthenticated, user?.role, navigate]);

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <Content style={{ padding: '24px 48px', maxWidth: '900px', margin: '0 auto', width: '100%' }}>
        {/* パンくずリスト */}
        <Breadcrumb
          style={{ marginBottom: '24px' }}
          items={[
            {
              href: '/',
              title: <HomeOutlined />,
            },
            {
              href: '/help',
              title: 'ヘルプ',
            },
            {
              title: topic?.title || '記事',
            },
          ]}
        />

        {/* 戻るボタン */}
        <Button
          icon={<LeftOutlined />}
          onClick={() => navigate('/help')}
          style={{ marginBottom: '16px' }}
        >
          ヘルプ一覧に戻る
        </Button>

        {/* コンテンツ */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Spin size="large" />
            <p style={{ marginTop: '16px', color: '#666' }}>読み込み中...</p>
          </div>
        )}

        {error && (
          <Alert
            type="error"
            message={error.statusCode === 403 ? 'アクセス権限がありません' : '読み込みエラー'}
            description={
              error.statusCode === 403
                ? 'この記事を閲覧する権限がありません。'
                : error.message
            }
            action={
              error.statusCode === 403 ? (
                <Button size="small" onClick={() => navigate('/login')}>
                  ログイン
                </Button>
              ) : (
                <Button size="small" onClick={() => window.location.reload()}>
                  再読み込み
                </Button>
              )
            }
            style={{ marginTop: '24px' }}
          />
        )}

        {!loading && !error && content && (
          <div style={{ backgroundColor: '#fff', borderRadius: '8px' }}>
            <HelpContent content={content} baseImagePath={baseImagePath} currentTopicId={articleId} />
          </div>
        )}
      </Content>
    </Layout>
  );
}
